import { ChangeEvent, useRef } from 'react';

import { InspectionDTO } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Path, useForm } from 'react-hook-form';
import { z } from 'zod';

import { ArrowLeftIcon } from '@/components/icons/arrow-left';
import { Form, FormFieldItem } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetHeader } from '@/components/ui/sheet';
import { useToast } from '@/components/ui/toaster';
import { AWAApiV2Service } from '@/services/awa-api-v2';

type FormSubmission = InspectionDTO;

export function ShareDrawer({
  open,
  onOpenChange,
  formSubmission: formSubmission,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formSubmission: FormSubmission;
}) {
  const toast = useToast();

  const inputRefEmailAddress = useRef<HTMLInputElement>(null);

  const shareFormSchema = z.object({
    email: z.string().min(1, { message: 'Required' }).email({ message: 'Invalid email' }),
  });

  const form = useForm<z.infer<typeof shareFormSchema>>({
    resolver: zodResolver(shareFormSchema),
    defaultValues: {
      email: '',
    },
  });

  const { mutateAsync: sendInspectionToEmail, isPending: isSendingInspectionToEmail } = useMutation({
    mutationFn: async (data: z.infer<typeof shareFormSchema>) => {
      if (!formSubmission) {
        throw new Error('Inspection not found');
      }

      await AWAApiV2Service.SendInspectionToEmail({
        inspectionId: formSubmission.id,
        email: data.email,
      });
    },
    onError: (error) => {
      toast.error('Unable to send Record. Please try again.');
    },
    onSuccess: () => {
      toast.success('Record sent successfully.');
      form.reset();
      onOpenChange(false);
    },
  });

  function handleSubmission(data: z.infer<typeof shareFormSchema>) {
    sendInspectionToEmail(data);
  }

  function handleError() {
    // Future use
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="mx-auto h-full w-full sm:max-w-[800px]"
        hideCloseIcon
        onInteractOutside={(e) => e.preventDefault()}
        onAnimationEnd={() => {
          inputRefEmailAddress.current?.focus();
        }}>
        <SheetHeader className="border-grey-3 m-0 border-b-2 p-0">
          <div className="flex h-[80px] w-full flex-row items-center justify-between gap-2 bg-white px-4 py-[25px]">
            <div className="mx-auto flex h-[30px] w-full flex-row items-center gap-2 p-0">
              <div
                onClick={() => {
                  onOpenChange(false);
                }}>
                <ArrowLeftIcon size={30} />
              </div>
              <div className="text-default-text text-base font-medium">Send as email</div>
            </div>
            <div>
              <Button
                variant="link"
                className={`text-default-green text-base font-medium`}
                onClick={() => {
                  form.handleSubmit(handleSubmission, handleError)();
                }}
                disabled={isSendingInspectionToEmail || !form.formState.isValid}>
                Send
              </Button>
            </div>
          </div>
        </SheetHeader>
        <div className="flex h-[calc(100vh-8rem)] flex-col overflow-y-auto">
          <div className="flex flex-col gap-2 px-6 pt-4 pb-6">
            <Form {...form}>
              <form>
                <FormFieldItem<z.infer<typeof shareFormSchema>, Path<z.infer<typeof shareFormSchema>>> control={form.control} name="email">
                  {({ value, onChange, error }) => (
                    <Input
                      ref={inputRefEmailAddress}
                      placeholder={'Enter email address ...'}
                      value={value as string}
                      onChange={onChange as unknown as (e: ChangeEvent<HTMLInputElement>) => void}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          form.handleSubmit(handleSubmission, handleError)();
                        }
                      }}
                      error={!!error}
                    />
                  )}
                </FormFieldItem>
              </form>
            </Form>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
